//
// Copyright (c) 2011-2023 by Curalate, Inc.
//

import {
  ILike2BuyV2DigestBannerRequestBody,
  ILike2BuyV2DigestBannerResult,
  ILike2BuyV2DigestBannerResponse,
  ILike2BuyV2CrawlBannerRequestBody,
  ILike2BuyV2CrawlBannerResponse,
  ILike2BuyV2CrawlBannerResult,
} from '@apis/like2buy-v2/like2BuyV2CustomizationsModel';
import {
  ILike2BuyV2Store,
  ILike2BuyV2StoreResponse,
  Like2BuyV2TemplateConfig,
} from '@apis/like2buy-v2/like2BuyV2StoreModel';
import Like2BuyV2StoreId from '@models/Like2BuyV2StoreId';
import { serializedGatewayFetch } from '@utils/sessionFetch';
import { ISerializedResponse } from '@utils/types/SerializedResponse';

function like2buyV2CustomizationsFetch(
  endpoint: string,
  init?: RequestInit,
  useFetchHeader?: boolean,
): Promise<ISerializedResponse> {
  return serializedGatewayFetch(
    'like2buy_gateway',
    `/api/v2/customizations${endpoint}`,
    init,
    useFetchHeader,
  );
}

/**
 *
 * Digests an image file for the given storeId's Like2BuyV2 customizations banner
 *
 * POST /api/v2/customizations/:storeId/banners/files
 */
export async function digestLike2BuyV2CustomizationsBanner(
  storeId: Like2BuyV2StoreId,
  body: ILike2BuyV2DigestBannerRequestBody,
): Promise<ILike2BuyV2DigestBannerResult> {
  const { file } = body;

  const formData = new FormData();
  formData.append(file.name, file);

  const res = await like2buyV2CustomizationsFetch(
    `/${storeId}/banners/files`,
    {
      method: 'POST',
      body: formData,
    },
    true,
  );
  const like2buyV2CustomizationsResponse =
    await res.json<ILike2BuyV2DigestBannerResponse>();

  return like2buyV2CustomizationsResponse.data;
}

/**
 *
 * Creates a crawled product for the given storeId's Like2BuyV2 customizations banner image and provided url
 *
 * POST /api/v2/customizations/:storeId/banners/products
 */
export async function createLike2BuyV2CustomizationsBannerCrawledProduct(
  storeId: Like2BuyV2StoreId,
  body: ILike2BuyV2CrawlBannerRequestBody,
): Promise<ILike2BuyV2CrawlBannerResult> {
  const res = await like2buyV2CustomizationsFetch(
    `/${storeId}/banners/products`,
    {
      method: 'POST',
      body: JSON.stringify(body),
    },
  );
  const like2buyV2CustomizationsResponse =
    await res.json<ILike2BuyV2CrawlBannerResponse>();

  return like2buyV2CustomizationsResponse.data;
}

/* Updates Like2Buy v2 store config for the given storeId
 *
 * PUT /api/v2/customizations/:storeId
 */
export async function updateLike2BuyV2StoreConfig({
  storeId,
  body,
}: {
  storeId: Like2BuyV2StoreId;
  body: Like2BuyV2TemplateConfig;
}): Promise<ILike2BuyV2Store | undefined> {
  const res = await like2buyV2CustomizationsFetch(`/${storeId}`, {
    body: JSON.stringify(body),
    method: 'PUT',
  });

  const like2buyV2StoreResponse = await res.json<ILike2BuyV2StoreResponse>();

  return like2buyV2StoreResponse.data;
}
